import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  hubService,
  type CreateToolDto,
  type TestToolResult,
  type Tool,
  type UpdateToolDto,
} from '../../services/hub.service';

type ToolFormState = {
  name: string;
  type: string;
  endpoint: string;
  priority: number;
  description: string;
  config: string;
};

const DEFAULT_FORM: ToolFormState = {
  name: '',
  type: 'mcp',
  endpoint: '',
  priority: 0,
  description: '',
  config: '{}',
};

function stringifyConfig(v: string): string {
  const trimmed = v.trim();
  if (!trimmed) return '{}';
  const parsed = JSON.parse(trimmed) as unknown;
  return JSON.stringify(parsed, null, 2);
}

export function BotToolsManager({ botId, system }: { botId: string; system: string }) {
  const { getToken } = useAuth();
  const [items, setItems] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Tool | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<ToolFormState>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [testTool, setTestTool] = useState<Tool | null>(null);
  const [testPayload, setTestPayload] = useState('{\n  "message": "hello"\n}');
  const [testTimeoutMs, setTestTimeoutMs] = useState(15000);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestToolResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0) || a.name.localeCompare(b.name)),
    [items],
  );

  useEffect(() => {
    void load();
  }, [botId, system]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const data = await hubService.listToolsByBot(botId, system, token);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tools');
    } finally {
      setLoading(false);
    }
  }

  function startCreate() {
    setCreating(true);
    setEditing(null);
    setForm(DEFAULT_FORM);
    setFormError(null);
  }

  function startEdit(tool: Tool) {
    setEditing(tool);
    setCreating(false);
    setFormError(null);
    setForm({
      name: tool.name ?? '',
      type: tool.type ?? 'mcp',
      endpoint: tool.endpoint ?? '',
      priority: tool.priority ?? 0,
      description: tool.description ?? '',
      config: tool.config ? JSON.stringify(JSON.parse(tool.config), null, 2) : '{}',
    });
  }

  function closeForm() {
    setCreating(false);
    setEditing(null);
    setFormError(null);
    setForm(DEFAULT_FORM);
  }

  async function saveForm() {
    setFormError(null);
    if (!form.name.trim()) {
      setFormError('Name is required');
      return;
    }

    let normalizedConfig = '{}';
    try {
      normalizedConfig = stringifyConfig(form.config);
    } catch (e) {
      setFormError(e instanceof Error ? `Invalid config JSON: ${e.message}` : 'Invalid config JSON');
      return;
    }

    setSaving(true);
    try {
      const token = getToken();
      if (editing) {
        const dto: UpdateToolDto = {
          name: form.name.trim(),
          type: form.type.trim() || 'mcp',
          endpoint: form.endpoint.trim() || undefined,
          description: form.description.trim() || undefined,
          priority: Number(form.priority) || 0,
          config: normalizedConfig,
        };
        await hubService.updateTool(editing.id, dto, token);
      } else {
        const dto: CreateToolDto = {
          system,
          botId,
          name: form.name.trim(),
          type: form.type.trim() || 'mcp',
          endpoint: form.endpoint.trim() || undefined,
          description: form.description.trim() || undefined,
          priority: Number(form.priority) || 0,
          config: normalizedConfig,
        };
        await hubService.createTool(dto, token);
      }
      closeForm();
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(tool: Tool) {
    if (!confirm(`Delete tool "${tool.name}"?`)) return;
    try {
      const token = getToken();
      await hubService.deleteTool(tool.id, token);
      await load();
      if (testTool?.id === tool.id) setTestTool(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  async function runTest() {
    if (!testTool) return;
    setTestError(null);
    setTestResult(null);
    let payload: Record<string, unknown> = {};
    try {
      payload = JSON.parse(testPayload || '{}') as Record<string, unknown>;
    } catch (e) {
      setTestError(e instanceof Error ? `Invalid test payload JSON: ${e.message}` : 'Invalid test payload JSON');
      return;
    }
    setTesting(true);
    try {
      const token = getToken();
      const result = await hubService.testTool(
        testTool.id,
        {
          payload,
          timeoutMs: Number(testTimeoutMs) || 15000,
          dryRun: true,
        },
        token,
      );
      setTestResult(result);
    } catch (e) {
      setTestError(e instanceof Error ? e.message : 'Test failed');
    } finally {
      setTesting(false);
    }
  }

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <p className="text-sm text-gray-600">
          Configure bot tools (MCP/API/RAG), edit existing definitions, and run custom test calls before using in
          production flows.
        </p>
      </div>

      {!creating && !editing ? (
        <button
          type="button"
          onClick={startCreate}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
        >
          Add Tool
        </button>
      ) : (
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
          <h3 className="font-medium text-gray-900">{editing ? 'Edit Tool' : 'Create Tool'}</h3>
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs text-gray-600">
              Name
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="text-xs text-gray-600">
              Type
              <input
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                placeholder="mcp | api | rag"
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="text-xs text-gray-600 sm:col-span-2">
              Endpoint
              <input
                value={form.endpoint}
                onChange={(e) => setForm((f) => ({ ...f, endpoint: e.target.value }))}
                placeholder="https://api.example.com/tool"
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="text-xs text-gray-600">
              Priority
              <input
                type="number"
                min={0}
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) || 0 }))}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="text-xs text-gray-600 sm:col-span-2">
              Description
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={2}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="text-xs text-gray-600 sm:col-span-2">
              Config (JSON)
              <textarea
                value={form.config}
                onChange={(e) => setForm((f) => ({ ...f, config: e.target.value }))}
                rows={8}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono"
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => void saveForm()}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={closeForm} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Endpoint</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Priority</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedItems.map((item) => (
              <tr key={item.id}>
                <td className="px-3 py-2">{item.name}</td>
                <td className="px-3 py-2">{item.type || '-'}</td>
                <td className="px-3 py-2 text-xs text-gray-600 max-w-[320px] truncate" title={item.endpoint || ''}>
                  {item.endpoint || '—'}
                </td>
                <td className="px-3 py-2">{item.priority ?? 0}</td>
                <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                  <button type="button" onClick={() => startEdit(item)} className="text-blue-600 hover:underline">
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTestTool(item);
                      setTestResult(null);
                      setTestError(null);
                    }}
                    className="text-blue-600 hover:underline"
                  >
                    Test
                  </button>
                  <button type="button" onClick={() => void handleDelete(item)} className="text-red-600 hover:underline">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedItems.length === 0 && <p className="p-4 text-sm text-gray-500">No tools configured for this bot.</p>}
      </div>

      {testTool && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
          <h3 className="font-medium text-gray-900">Custom Test: {testTool.name}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <p className="text-xs text-gray-500 sm:col-span-2">
              Calls the tool&apos;s saved endpoint ({testTool.endpoint || 'none set'}). Platform admins only.
            </p>
            <label className="text-xs text-gray-600">
              Timeout ms
              <input
                type="number"
                min={1000}
                value={testTimeoutMs}
                onChange={(e) => setTestTimeoutMs(Number(e.target.value) || 15000)}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="text-xs text-gray-600 sm:col-span-2">
              Test payload (JSON)
              <textarea
                value={testPayload}
                onChange={(e) => setTestPayload(e.target.value)}
                rows={8}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono"
              />
            </label>
          </div>
          {testError && <p className="text-sm text-red-600">{testError}</p>}
          {testResult && (
            <div className={`rounded border p-3 ${testResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <p className="text-sm font-medium">{testResult.success ? 'Success' : 'Failed'}</p>
              <p className="text-sm mt-1 whitespace-pre-wrap">{testResult.text}</p>
              {testResult.data != null && (
                <pre className="mt-2 text-xs overflow-auto max-h-64 bg-white/70 rounded p-2">
                  {JSON.stringify(testResult.data, null, 2)}
                </pre>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void runTest()}
              disabled={testing}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
            >
              {testing ? 'Testing...' : 'Run Test'}
            </button>
            <button
              type="button"
              onClick={() => setTestTool(null)}
              className="px-3 py-1.5 text-sm bg-gray-200 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

